import axiosInstance from '@/lib/enhancedAxiosInstance';
import { toast } from 'sonner';
import { paths } from '@/lib/apiPaths';
import { logError, logWarn } from '@/utils/errorHandler';

/**
 * Interface for Organization objects returned from the API
 */
export interface Organization {
  id: number;
  name: string;
  default_locale: string;
  default_channel: {
    id: number;
    code: string;
    name: string;
  };
  default_currency?: {
    iso_code: string;
    symbol: string;
    name: string;
    decimals: number;
  } | null;
  default_currency_id?: string | null;
  default_price_type?: {
    id: number;
    code: string;
    label: string;
  } | null;
  default_price_type_id?: number | null;

  // Plan and billing information
  plan: string;
  billing_cycle: string;

  // Stripe integration
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  is_active_subscription: boolean;
  subscription_started_at?: string | null;
  subscription_ends_at?: string | null;

  // Trial logic
  is_trial: boolean;
  trial_started_at?: string | null;
  trial_ends_at?: string | null;
  is_trial_expired: boolean;

  // Computed properties
  is_subscription_active?: boolean;
  is_trial_active?: boolean;
  subscription_status?: string;
  days_until_trial_expires?: number | null;
}

/**
 * Get current organization ID from user context or localStorage fallback
 */
const getCurrentOrgId = (): number => {
  // First try to get from user context (check if we're in a browser environment)
  if (typeof window !== 'undefined') {
    try {
      // Try to get user data from localStorage (where AuthContext stores it)
      const userDataString = localStorage.getItem('user') || localStorage.getItem('userData');
      if (userDataString) {
        const userData = JSON.parse(userDataString);
        if (userData.organization_id) {
          return Number(userData.organization_id);
        }
      }
    } catch (error) {
      logWarn('Failed to parse user data from localStorage', {
        component: 'organizationService',
        action: 'getCurrentOrgId',
        metadata: { error },
      });
    }

    // Fallback to currentOrgId in localStorage
    const orgId = localStorage.getItem('currentOrgId');
    if (orgId) {
      return Number(orgId);
    }

    logWarn('No organization ID found in localStorage, using default', {
      component: 'organizationService',
      action: 'getCurrentOrgId',
      metadata: { fallbackOrgId: 1 },
    });
  }

  return 1; // Default fallback
};

/**
 * Service for managing organization data
 */
const organizationService = {
  /**
   * Get current organization
   * @returns Promise resolving to Organization object
   */
  getOrganization: async (): Promise<Organization> => {
    try {
      const orgId = getCurrentOrgId();
      const { data } = await axiosInstance.get(paths.organizations.byId(orgId));
      return data;
    } catch (error) {
      logError(
        'Failed to fetch organization data',
        {
          component: 'organizationService',
          action: 'getOrganization',
          metadata: { orgId: getCurrentOrgId(), error },
        },
        { showToast: false, userFacing: false }
      );
      toast.error('Failed to load organization settings');
      throw error;
    }
  },

  /**
   * Update organization settings
   * @param changes Object with the fields to update
   * @returns Promise resolving to updated Organization object
   */
  updateOrganization: async (
    changes: Partial<
      Pick<Organization, 'name' | 'default_locale'> & {
        default_channel_id?: number;
      }
    >
  ): Promise<Organization> => {
    try {
      const orgId = getCurrentOrgId();
      const { data } = await axiosInstance.patch(paths.organizations.byId(orgId), changes);
      toast.success('Organization settings updated successfully');
      return data;
    } catch (error) {
      logError(
        'Failed to update organization',
        {
          component: 'organizationService',
          action: 'updateOrganization',
          metadata: { orgId: getCurrentOrgId(), changes, error },
        },
        { showToast: false, userFacing: false }
      );
      toast.error('Failed to update organization settings');
      throw error;
    }
  },
};

export default organizationService;
